import { FC } from 'react'
import { Info, Logo } from '@components/icons'
import { Button } from '@corratech/pylot-ui'
import cn from 'classnames'
import s from './Auth.module.scss'
import { FormInput } from '@components/common/FormInput'
import { Checkbox } from '@components/common/Checkbox'
import { AlertMessage } from '@components/common/AlertMessage'
import dynamic from 'next/dynamic'
import {
    MIN_BIRTH_DATE,
    PHONE_CODE,
    SignUpPrefill,
    useSignUpForm
} from './hooks/useSignUpForm'
import { CHILE_PHONE_LENGTH } from '@lib/utils/chilePhone'

// @ts-ignore
const RegionComunas = dynamic(() =>
    import('@components/common/RegionComunas').then((res) => res.RegionComunas)
)

const SignUpView: FC<{
    data?: SignUpPrefill
    className?: string
}> = ({ data, className = '' }) => {
    const {
        t,
        state,
        maxBirthDate,
        setField,
        toggle,
        setRegion,
        setComuna,
        handleSignup,
        handleBirthdayChange,
        handlePhoneChange,
        handleAddressPhoneChange,
        handleRutChange,
        setModalView
    } = useSignUpForm(data)

    const {
        email,
        firstName,
        lastName,
        phone,
        rut,
        birthday,
        password,
        confirmPassword,
        addShippingAddress,
        acceptOffersByEmail,
        acceptTerms,
        saveAddressAs,
        region,
        comuna,
        address,
        addressNumber,
        addressComplement,
        zipcode,
        addressPhone,
        loading,
        message,
        disabled,
        formErrors
    } = state

    return (
        <form
            onSubmit={handleSignup}
            className={cn(s['sign-up-view'], 'sign-up-form', className)}
            noValidate
        >
            <div className={cn(s['form-logo'], 'form-logo')}>
                <Logo />
            </div>
            {message && (
                <div className={s['message-wrap']}>
                    <AlertMessage type="danger">{message}</AlertMessage>
                </div>
            )}
            <div className="form-group" key="email">
                <FormInput
                    type="email"
                    id="email"
                    name={t('auth|Email')}
                    defaultValue={data?.email ?? ''}
                    value={email}
                    onChange={(v) => setField('email', v)}
                    error={formErrors?.email}
                />
            </div>

            <div className="form-group" key="name">
                <FormInput
                    type="text"
                    id="firstName"
                    name={t('auth|First Name')}
                    defaultValue={data?.firstname ?? ''}
                    value={firstName}
                    onChange={(v) => setField('firstName', v)}
                    error={formErrors?.firstName}
                />
            </div>

            <div className="form-group" key="lastName">
                <FormInput
                    type="text"
                    id="lastName"
                    name={t('auth|Last Name')}
                    defaultValue={data?.lastname ?? ''}
                    value={lastName}
                    onChange={(v) => setField('lastName', v)}
                    error={formErrors?.lastName}
                />
            </div>

            <div
                className="form-group flex items-center justify-items-center"
                key="phone"
            >
                <span className="p-4 pb-5 pl-0 text-base">{PHONE_CODE}</span>
                <FormInput
                    type="tel"
                    inputMode="numeric"
                    id="phone"
                    name={t('auth|Phone')}
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={CHILE_PHONE_LENGTH}
                    error={formErrors?.phone}
                />
            </div>
            <div className="form-group" key="rut">
                <div className={s.rutInputContainer}>
                    <input
                        className={cn(
                            `appearance-none w-full border-b ${
                                formErrors?.rut
                                    ? 'border-alert-danger'
                                    : 'border-p-7'
                            } px-0 mb-2 focus:shadow-none focus:outline-none focus-visible:border-b focus-visible:shadow-none`,
                            s.rutInput
                        )}
                        id="rut"
                        // eslint-disable-next-line i18next/no-literal-string
                        name="rut"
                        value={rut}
                        onChange={handleRutChange}
                        type="text"
                        placeholder=" "
                    />

                    <label htmlFor="rut" className={s.inputLabel}>
                        {t('RUT')}
                    </label>
                </div>
                {formErrors?.rut && (
                    <span className="text-alert-danger text-xs leading-4 font-normal">
                        {formErrors?.rut}
                    </span>
                )}
            </div>
            <div className="form-group" key="birthday">
                <FormInput
                    type="date"
                    id="birthday"
                    name={t('auth|Birthday')}
                    value={birthday}
                    min={MIN_BIRTH_DATE}
                    max={maxBirthDate}
                    onChange={handleBirthdayChange}
                    error={formErrors?.birthday}
                />
            </div>

            <div className="form-group" key="password">
                <FormInput
                    type="password"
                    id="password"
                    name={t('auth|Password')}
                    // eslint-disable-next-line i18next/no-literal-string
                    autoComplete="new-password"
                    value={password}
                    onChange={(v) => setField('password', v)}
                    error={formErrors?.password}
                />
            </div>

            <div className="form-group" key="confirmPassword">
                <FormInput
                    type="password"
                    id="confirmPassword"
                    name={t('auth|Confirm Password')}
                    // eslint-disable-next-line i18next/no-literal-string
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(v) => setField('confirmPassword', v)}
                    error={formErrors?.confirmPassword}
                />
            </div>

            <span className="text-accents-8 mb-4">
                <span className="inline-block align-middle ">
                    <Info width="15" height="15" />
                </span>{' '}
                <span className="leading-6 text-sm">
                    <strong>{t('auth|Info')}</strong>: {t('auth|Password tip')}
                </span>
            </span>

            <div className="form-group">
                <Checkbox
                    id="shippingAddress"
                    checked={addShippingAddress}
                    onChange={() => toggle('addShippingAddress')}
                    label={t('auth|Add my shipping address')}
                />
            </div>

            {addShippingAddress && (
                <div className="my-8">
                    <div className="form-group">
                        <FormInput
                            type="text"
                            id="saveAddressAs"
                            name={t('auth|Save Address as')}
                            value={saveAddressAs}
                            onChange={(v) => setField('saveAddressAs', v)}
                        />
                    </div>

                    <div className="form-group">
                        <RegionComunas
                            // @ts-ignore
                            region={region}
                            comuna={comuna}
                            onComunaChange={setComuna}
                            onRegionChange={setRegion}
                            comunaError={formErrors?.comuna}
                            regionError={formErrors?.region}
                        />
                    </div>

                    <div className="form-group">
                        <FormInput
                            type="text"
                            id="address"
                            name={t('auth|Address')}
                            value={address}
                            onChange={(v) => setField('address', v)}
                            error={formErrors?.address}
                        />
                    </div>
                    <div className="form-group">
                        <FormInput
                            type="number"
                            id="addressNumber"
                            name={t('auth|Number')}
                            value={addressNumber}
                            onChange={(v) => setField('addressNumber', v)}
                            error={formErrors?.addressNumber}
                        />
                    </div>
                    <div className="form-group">
                        <FormInput
                            type="text"
                            id="addressComplement"
                            name={t('auth|Appt., office, etc')}
                            value={addressComplement}
                            onChange={(v) => setField('addressComplement', v)}
                        />
                    </div>
                    <div className="form-group">
                        <FormInput
                            type="text"
                            id="zipcode"
                            name={t('auth|Zipcode')}
                            value={zipcode}
                            onChange={(v) => setField('zipcode', v)}
                            required={false}
                            error={formErrors?.zipcode}
                        />
                    </div>
                    <div className="form-group flex items-center justify-items-center">
                        <span className="p-4 pb-5 pl-0 text-base">
                            {PHONE_CODE}
                        </span>
                        <FormInput
                            type="tel"
                            inputMode="numeric"
                            id="addressPhone"
                            name={t('auth|Phone')}
                            value={addressPhone}
                            onChange={handleAddressPhoneChange}
                            maxLength={CHILE_PHONE_LENGTH}
                            error={formErrors?.addressPhone}
                        />
                    </div>
                </div>
            )}

            <div className="form-group">
                <Checkbox
                    id="acceptOffersByEmail"
                    label={t('auth|I want to receive offers by email')}
                    checked={acceptOffersByEmail}
                    onChange={() => toggle('acceptOffersByEmail')}
                />
            </div>

            <div className="form-group">
                <Checkbox
                    id="acceptTerms"
                    label={
                        <a
                            className={cn(s['accept-terms-link'], 'link')}
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://casaideas.zendesk.com/hc/es/articles/360048436193-www-casaideas-cl:"
                        >
                            {t('auth|I accept the terms and conditions')}
                        </a>
                    }
                    checked={acceptTerms}
                    onChange={() => toggle('acceptTerms')}
                />
                <span className="text-alert-danger text-xs leading-4 font-normal block mt-2">
                    {formErrors?.acceptTerms}
                </span>
            </div>

            <div className={cn(s['form-actions'], 'form-actions', 'mt-4')}>
                <Button
                    variant="slim"
                    type="submit"
                    loading={loading}
                    disabled={disabled}
                >
                    {t('auth|Sign Up here')}
                </Button>

                <div className={s['form-below-options']}>
                    <span className="text-accents-7">
                        {t('auth|Do you have an account?')},
                    </span>
                    <span
                        className={cn(s['login-link'], 'login-link')}
                        onClick={() => setModalView('LOGIN_VIEW')}
                        aria-hidden="true"
                    >
                        {t('auth|Log In here')}
                    </span>
                </div>
            </div>
        </form>
    )
}

export default SignUpView
